import React, { useState, useRef, useEffect } from 'react'

interface AudioSystemProps {
  onAudioChange?: (isPlaying: boolean, currentTrack?: string) => void
}

export const AudioSystem: React.FC<AudioSystemProps> = ({ onAudioChange }) => {
  const [currentTrack, setCurrentTrack] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(0.8)
  const audioRef = useRef<HTMLAudioElement>(null)

  const audioTracks = {
    'intro-narration': {
      title: 'Welcome Introduction',
      file: '/audio/intro-narration.mp3'
    },
    'attorney-scam-exposed': {
      title: 'Attorney Scam Exposed',
      file: '/audio/attorney-scam-exposed.mp3'
    },
    'diy-solution': {
      title: 'DIY Solution Benefits',
      file: '/audio/diy-solution.mp3'
    },
    'exit-club-benefits': {
      title: 'Exit Club Benefits',
      file: '/audio/exit-club-benefits.mp3'
    }
  }

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handlePlay = () => {
      setIsPlaying(true)
      onAudioChange?.(true, currentTrack || undefined)
    }

    const handlePause = () => {
      setIsPlaying(false)
      onAudioChange?.(false, currentTrack || undefined)
    }

    const handleEnded = () => {
      setIsPlaying(false)
      setCurrentTrack(null)
      onAudioChange?.(false, undefined)
    }

    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('ended', handleEnded)

    return () => {
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [currentTrack, onAudioChange])

  const playAudio = (trackId: string) => {
    const audio = audioRef.current
    const track = audioTracks[trackId as keyof typeof audioTracks]
    
    if (!audio || !track) {
      console.error('Audio element or track not found:', trackId)
      return
    }

    try {
      // If same track is playing, pause it
      if (currentTrack === trackId && isPlaying) {
        audio.pause()
        return
      }

      // Load new track
      setCurrentTrack(trackId)
      audio.src = track.file
      audio.volume = volume
      
      // Play the audio
      audio.play().catch((error) => {
        console.error('Audio play failed:', error)
        setIsPlaying(false)
        setCurrentTrack(null)
        onAudioChange?.(false, undefined)
      })
    } catch (error) {
      console.error('Audio system error:', error)
    }
  }

  const stopAudio = () => {
    const audio = audioRef.current
    if (audio) {
      audio.pause()
      audio.currentTime = 0
      setCurrentTrack(null)
      setIsPlaying(false)
      onAudioChange?.(false, undefined)
    }
  }

  const togglePlayPause = () => {
    const audio = audioRef.current
    if (!audio || !currentTrack) return

    if (isPlaying) {
      audio.pause()
    } else {
      audio.play().catch(console.error)
    }
  }

  return (
    <div className="audio-system">
      <audio 
        ref={audioRef}
        preload="none"
        style={{ display: 'none' }}
      />
      
      {/* Audio Controls */}
      <div className="bg-black/80 backdrop-blur-sm rounded-lg p-4 border border-white/20">
        <div className="text-white text-center mb-3">
          <h3 className="font-bold text-lg mb-2">🎧 Audio Narration Available</h3>
          <p className="text-sm text-gray-300">
            Listen to our AI narrator explain the timeshare attorney scam
          </p>
        </div>
        
        {/* Track Selection */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          {Object.entries(audioTracks).map(([trackId, track]) => (
            <button
              key={trackId}
              onClick={() => playAudio(trackId)}
              className={`text-xs py-2 px-3 rounded transition-colors ${
                currentTrack === trackId && isPlaying
                  ? 'bg-green-600 text-white'
                  : currentTrack === trackId
                  ? 'bg-yellow-600 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {currentTrack === trackId && isPlaying ? '⏸️' : '▶️'} {track.title}
            </button>
          ))}
        </div>
        
        {/* Current Track Info */}
        {currentTrack && (
          <div className="bg-white/10 rounded p-3 mb-3">
            <div className="flex items-center justify-between text-white">
              <span className="text-sm font-medium">
                Now: {audioTracks[currentTrack as keyof typeof audioTracks]?.title}
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={togglePlayPause}
                  className="bg-blue-600 hover:bg-blue-700 text-white p-1 rounded text-xs"
                >
                  {isPlaying ? '⏸️' : '▶️'}
                </button>
                <button
                  onClick={stopAudio}
                  className="bg-red-600 hover:bg-red-700 text-white p-1 rounded text-xs"
                >
                  ⏹️
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Volume Control */}
        <div className="flex items-center space-x-2">
          <span className="text-white text-xs">🔊</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={volume}
            onChange={(e) => {
              const newVolume = parseFloat(e.target.value)
              setVolume(newVolume)
              if (audioRef.current) {
                audioRef.current.volume = newVolume
              }
            }}
            className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-white text-xs">{Math.round(volume * 100)}%</span>
        </div>
        
        {!currentTrack && (
          <p className="text-center text-gray-400 text-xs mt-2">
            Click any audio button above to start listening
          </p>
        )}
      </div>
    </div>
  )
}

export default AudioSystem
